import { Component, h } from "preact";
import { connectWithTranslations } from "../../../common/helpers/translationsHelpers";
import Spinner from "../../../common/components/Spinner";
import IconBell from "../../../common/components/icons/IconBell";
import IconBellSolid from "../../../common/components/icons/IconBellSolid";
import IconCalendarPlus from "../../../common/components/icons/IconCalendarPlus";
import IconChartBar from "../../../common/components/icons/IconChartBar";
import IconFileSolid from "../../../common/components/icons/IconFileSolid";
import IconInfoCircle from "../../../common/components/icons/IconInfoCircle";
import { urlFactory, downloadCalendar, formatTemplate } from "../../helpers";
import { GTM_ID, GA4EVENT } from "../../constants";
import actions from "../../actions";
import renderNumerical from "../../helpers/render";
import { getDeviation } from "../../helpers/deviation";
import { CALENDAR_VIEW } from "../../../calendar-common/constants";
import { enabledCalendarNotifications } from "../../../calendar-common/helpers/calendar";
import SpeechTrackerModal from "../../components/modal/SpeechTrackerModal";
import SpeechScore from "./SpeechScore";
import SpeechAverageScore from "./SpeechAverageScore";
import eventTimeCheck from "../../helpers/eventTimeCheck";
import { getSpeechScore } from "../../helpers/getSpeechScore";

class EventRowDetailMobile extends Component {
    constructor(props) {
        super(props);
        this.renderDetail = this.renderDetail.bind(this);
        this.openModalDetails = this.openModalDetails.bind(this);
        this.state = {
            showSpeechTrackerModal: false,
        };
    }

    toggleSpeechTrackerModal = () => {
        this.setState((prevState) => ({
            showSpeechTrackerModal: !prevState.showSpeechTrackerModal,
        }));
    };

    renderError() {
        return (
            <tr>
                <td colSpan="5">Error fetching event detail</td>
            </tr>
        );
    }

    renderFetching() {
        return (
            <tr>
                <td colSpan="5">
                    <Spinner />
                </td>
            </tr>
        );
    }

    openModalDetails() {
        const {
            event: { id },
            showEventDateModalMenu,
        } = this.props;
        showEventDateModalMenu(id);
    }

    renderSpeechScore({ isSpeechScoreLocked, speechScore, hasSpeechData, isInProgress }) {
        const { translate } = this.props;

        return (
            <>
                {isSpeechScoreLocked && (
                    <>
                        <p class="fxs_c_unlock">
                            <span>{translate("DetailEventDateUnlock")}</span>
                            <a
                                href="/account/login?uriToRedirect=%2Feconomic-calendar%3Fuser_action%3Dlogin"
                                class="fxs_c_unlock_btn"
                                data-ga4event-clk-name="features-calendar_click"
                                data-ga4event-element="log-in"
                            >
                                {translate("LogIn")}
                            </a>
                        </p>
                        <p
                            class="fxs_c_speed_tracker_mean"
                            onClick={this.toggleSpeechTrackerModal}
                        >
                            <IconInfoCircle />
                            <span>{translate("SpeechTrackerMean")}</span>
                        </p>
                    </>
                )}
                {!isSpeechScoreLocked && hasSpeechData && (
                    <div className="fxs_c_speech_score_detail_wrapper">
                        {speechScore?.isVotingMember != null && (
                            <p class="fxs_c_speech_tracker_score">
                                <span>{translate("Voting")}</span>
                                <span className="fxs_c_speech_score">
                                    {speechScore?.isVotingMember ? "Yes" : "No"}
                                </span>
                            </p>
                        )}
                        <p class="fxs_c_speech_tracker_score">
                            <span>
                                {translate("SpeechTrackerScoreLabel").concat(": ")}
                            </span>
                            {speechScore?.score ? (
                                <SpeechScore speechScore={speechScore} />
                            ) : isInProgress ? (
                                <span className="fxs_c_speech_score_label">
                                    {translate("InProgress")}
                                </span>
                            ) : (
                                <span className="fxs_c_speech_score">-</span>
                            )}
                        </p>
                        <p class="fxs_c_speech_tracker_score">
                            <span>{translate("Average")}</span>
                            <SpeechAverageScore
                                speechScore={speechScore}
                                includeLabel={true}
                            />
                        </p>
                    </div>
                )}
            </>
        );
    }

    renderDetail(eventDateDetailData, eventTimeReached) {
        const {
            canBeSubscribed,
            isSubscribed,
            subscribeToEvent,
            unsubscribeToEvent,
            translate,
            config,
            settings: { timezoneOffset, view },
            event,
            event: {
                actual,
                prefix,
                percentage,
                potency,
                consensus,
                previous,
                ratioDeviation,
                revised,
                isBetterThanExpected,
                speech,
                isSpeech,
                isReport,
                isAllDay,
            },
        } = this.props;

        let speechScore = null;
        let isSpeechScoreLocked = false;

        const hasScore = event?.speech?.score ?? false;
        const hasSpeechData =
            event?.speech?.score ?? event?.speech?.averageScore ?? false;

        const isSpeechScoreEnabled = config.enableSpeechScore;

        const isInProgress =
            !hasScore &&
            event?.isScoreTrackable &&
            isSpeechScoreEnabled &&
            eventTimeReached;

        if (isSpeech && isSpeechScoreEnabled && hasSpeechData) {
            speechScore = getSpeechScore(event);
            isSpeechScoreLocked = config.isLocked();
        }

        const isOneColumn = isSpeech || isReport || isAllDay;

        return (
            <tr>
                <td colSpan="5" class="fxs_c_detail">
                    {isSpeechScoreEnabled &&
                        this.renderSpeechScore({
                            isSpeechScoreLocked,
                            speechScore,
                            hasSpeechData,
                            isInProgress,
                        })}
                    {this.state.showSpeechTrackerModal && (
                        <SpeechTrackerModal
                            toggleSpeechTrackerModal={this.toggleSpeechTrackerModal}
                        />
                    )}
                    {!isOneColumn && (
                        <div class="fxs_c_detail_data">
                            <div class="fxs_c_detail_data_item">
                                <header class="fxs_c_detail_data_header">
                                    {translate("HeaderActual")}
                                </header>
                                <p
                                    class={`fxs_c_detail_data_data  ${
                                        typeof isBetterThanExpected === "boolean"
                                            ? isBetterThanExpected
                                                ? "fxs_c_positive"
                                                : "fxs_c_negative"
                                            : ""
                                    }`}
                                >
                                    {renderNumerical(
                                        actual,
                                        prefix,
                                        percentage,
                                        potency,
                                        translate
                                    )}
                                </p>
                            </div>
                            {view != CALENDAR_VIEW.Configure_View_Basic && (
                                <div class="fxs_c_detail_data_item">
                                    <header class="fxs_c_detail_data_header">
                                        {translate("RowDeviation")}.
                                    </header>
                                    <p class="fxs_c_detail_data_data">
                                        {ratioDeviation !== null &&
                                        ratioDeviation !== undefined
                                            ? ratioDeviation.toFixed(2)
                                            : getDeviation(actual, consensus)}
                                    </p>
                                </div>
                            )}
                            <div class="fxs_c_detail_data_item">
                                <header class="fxs_c_detail_data_header">
                                    {translate("RowConsensus")}
                                </header>
                                <p class="fxs_c_detail_data_data">
                                    {renderNumerical(
                                        consensus,
                                        prefix,
                                        percentage,
                                        potency,
                                        translate
                                    )}
                                </p>
                            </div>
                            <div class="fxs_c_detail_data_item">
                                <header class="fxs_c_detail_data_header">
                                    {translate("RowPrevious")}
                                </header>
                                <p class="fxs_c_detail_data_data">
                                    {typeof revised !== "undefined" &&
                                        revised !== null &&
                                        renderNumerical(
                                            revised,
                                            prefix,
                                            percentage,
                                            potency,
                                            translate
                                        )}
                                    {typeof revised === "undefined" ||
                                        (revised === null &&
                                            renderNumerical(
                                                previous,
                                                prefix,
                                                percentage,
                                                potency,
                                                translate
                                            ))}
                                </p>
                            </div>
                        </div>
                    )}
                    <div class="fxs_c_detail_buttons">
                        <button
                            type="button"
                            class={`fxs_c_detail_button fxs_c_detail_toggled_button`}
                            onClick={() => {
                                this.openModalDetails(this);
                            }}
                        >
                            <IconFileSolid />
                        </button>
                        <button
                            type="button"
                            class={`fxs_c_detail_button fxs_c_detail_toggled_button`}
                            onClick={() => {
                                window.open(
                                    formatTemplate(
                                        config.dashboardUrl,
                                        {
                                            eventId: eventDateDetailData.eventId,
                                            timezoneOffset,
                                        },
                                        "_blank"
                                    )
                                );
                            }}
                            data-gtmid={
                                config.tracking &&
                                GTM_ID.OPEN_DASHBOARD(eventDateDetailData.id)
                            }
                            data-ga4event-clk-name={
                                config.tracking && GA4EVENT.NAMES.FEATURES_CALENDAR
                            }
                            data-ga4event-element={
                                config.tracking && GA4EVENT.ELEMENTS.OPEN_DASHBOARD
                            }
                            data-ga4event-eventdateid={eventDateDetailData.id}
                        >
                            <IconChartBar />
                        </button>
                        <button
                            type="button"
                            class={`fxs_c_detail_button fxs_c_detail_toggled_button`}
                            data-gtmid={
                                config.tracking &&
                                GTM_ID.ADD_EVENT(eventDateDetailData.id)
                            }
                            data-ga4event-clk-name={
                                config.tracking && GA4EVENT.NAMES.FEATURES_CALENDAR
                            }
                            data-ga4event-element={
                                config.tracking && GA4EVENT.ELEMENTS.ADD_EVENT
                            }
                            data-ga4event-eventdateid={eventDateDetailData.id}
                            onClick={() =>
                                downloadCalendar(
                                    urlFactory.getEventDateICalendar(
                                        config,
                                        eventDateDetailData.id
                                    ),
                                    "text/calendar"
                                )
                            }
                        >
                            <IconCalendarPlus />
                        </button>
                        <button
                            type="button"
                            class={
                                isSubscribed
                                    ? "fxs_c_detail_button fxs_c_detail_button_active"
                                    : "fxs_c_detail_button"
                            }
                            data-gtmid={
                                config.tracking && isSubscribed
                                    ? GTM_ID.NOTIFY_ME_UNSUBSCRIPTION(
                                          eventDateDetailData.id
                                      )
                                    : GTM_ID.NOTIFY_ME_SUBSCRIPTION(
                                          eventDateDetailData.id
                                      )
                            }
                            data-ga4event-clk-name={
                                config.tracking && GA4EVENT.NAMES.FEATURES_CALENDAR
                            }
                            data-ga4event-element={
                                config.tracking && isSubscribed
                                    ? GA4EVENT.ELEMENTS.NOTIFY_ME_UNSUBSCRIPTION
                                    : GA4EVENT.ELEMENTS.NOTIFY_ME_SUBSCRIPTION
                            }
                            data-ga4event-eventdateid={eventDateDetailData.id}
                            onClick={() => {
                                if (isSubscribed)
                                    unsubscribeToEvent(eventDateDetailData.eventId);
                                else {
                                    subscribeToEvent(eventDateDetailData.eventId);
                                }
                            }}
                            disabled={
                                !canBeSubscribed && enabledCalendarNotifications(config)
                            }
                        >
                            {isSubscribed && enabledCalendarNotifications(config) ? (
                                <IconBellSolid />
                            ) : (
                                <IconBell />
                            )}
                        </button>
                    </div>
                </td>
            </tr>
        );
    }

    render({
        event,
        eventDateDetails,
        eventDateDetailMinimalShown,
        showEventNotifyModal,
        eventTimeReached,
    }) {
        const { id } = event;
        const isVisible =
            eventDateDetailMinimalShown && eventDateDetailMinimalShown === id;
        const eventDateDetail = eventDateDetails.hasOwnProperty(id)
            ? eventDateDetails[id]
            : null;

        if (!isVisible || !eventDateDetail) {
            return null;
        }
        if (eventDateDetail.error) {
            return this.renderError();
        }
        if (eventDateDetail.isFetching) {
            return this.renderFetching();
        }
        return this.renderDetail(eventDateDetail.data, eventTimeReached);
    }
}

export default connectWithTranslations(
    "eventDateDetails,eventDateDetailMinimalShown,config,settings",
    actions
)(eventTimeCheck(EventRowDetailMobile));
